import { useEffect, useState } from "react";
import { GraduationCap, Loader2, Trash2, Check, CheckCircle2, AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useDetachEleve, useUpdateEleveLien, useEleveSoldes } from "@/lib/backoffice-hooks";
import { formatXof, formatDate } from "@/lib/format";
import type { LienParente, MyEleve, MyPaiementRow } from "@/lib/api";

export interface ChildDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  child: MyEleve;
  /** Paiements (déjà chargés au niveau dashboard) — filtrés ici par eleveId. */
  paiements: MyPaiementRow[];
}

const LIENS: LienParente[] = ["PERE", "MERE", "TUTEUR", "TUTRICE", "AUTRE"];
const LIEN_LABEL: Record<LienParente, string> = {
  PERE: "Père",
  MERE: "Mère",
  TUTEUR: "Tuteur",
  TUTRICE: "Tutrice",
  AUTRE: "Autre",
};

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-bold uppercase tracking-wide text-navy/45">{label}</dt>
      <dd className="text-sm text-navy">{value}</dd>
    </div>
  );
}

export default function ChildDetailDialog({
  open,
  onOpenChange,
  child,
  paiements,
}: ChildDetailDialogProps) {
  const detach = useDetachEleve();
  const updateLien = useUpdateEleveLien();
  // Soldes de l'enfant — chargés uniquement quand la modale est ouverte.
  const soldes = useEleveSoldes(child.id, { enabled: open });

  const [lien, setLien] = useState<LienParente | "">("");
  const [confirmRetrait, setConfirmRetrait] = useState(false);

  // Réinitialise l'état local à chaque ouverture (lien depuis l'élève courant).
  useEffect(() => {
    if (open) {
      setLien((child.lien as LienParente | null) ?? "");
      setConfirmRetrait(false);
    }
  }, [open, child.lien]);

  // Paiements de CET enfant uniquement (réutilise les données déjà chargées).
  const childPaiements = paiements.filter((p) => p.eleveId === child.id);

  function submitLien(value: LienParente) {
    setLien(value);
    updateLien.mutate({ id: child.id, lien: value });
  }

  function submitRetrait() {
    detach.mutate(child.id, {
      onSuccess: () => onOpenChange(false),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-lg flex-col gap-0 overflow-hidden p-0">
        <div className="shrink-0 bg-navy px-6 py-5 text-white">
          <DialogTitle className="flex items-center gap-2 font-display text-xl font-bold text-white">
            <GraduationCap size={22} /> {child.prenoms} {child.nom}
          </DialogTitle>
          <DialogDescription className="mt-1 text-sm text-white/70">
            Détail de l'enfant, lien de parenté et paiements associés.
          </DialogDescription>
        </div>

        <div className="space-y-6 overflow-y-auto px-6 pb-6 pt-5">
          {/* ---------- Infos ---------- */}
          <dl className="grid grid-cols-2 gap-4">
            <Info label="Nom" value={child.nom} />
            <Info label="Prénoms" value={child.prenoms} />
            {child.matricule && <Info label="Matricule" value={child.matricule} />}
            {child.sexe && (
              <Info
                label="Sexe"
                value={child.sexe === "M" ? "Masculin" : child.sexe === "F" ? "Féminin" : child.sexe}
              />
            )}
            {child.dateNaissance && (
              <Info label="Date de naissance" value={formatDate(child.dateNaissance)} />
            )}
            {child.lieuNaissance && (
              <Info label="Lieu de naissance" value={child.lieuNaissance} />
            )}
            {child.ecoleNom && <Info label="École" value={child.ecoleNom} />}
            {child.classeLibelle && <Info label="Classe" value={child.classeLibelle} />}
            {child.statut && <Info label="Statut" value={child.statut} />}
          </dl>

          {/* ---------- Lien de parenté ---------- */}
          <div>
            <label
              htmlFor="child-detail-lien"
              className="text-xs font-bold uppercase tracking-wide text-navy/45"
            >
              Lien de parenté
            </label>
            <select
              id="child-detail-lien"
              value={lien}
              disabled={updateLien.isPending}
              onChange={(e) => submitLien(e.target.value as LienParente)}
              className="mt-1 w-full rounded-xl border border-navy/15 bg-white px-4 py-2.5 text-navy focus:border-coral focus:outline-none focus:ring-2 focus:ring-coral/20 disabled:opacity-50"
            >
              <option value="">Non précisé</option>
              {LIENS.map((l) => (
                <option key={l} value={l}>
                  {LIEN_LABEL[l]}
                </option>
              ))}
            </select>
          </div>

          {/* ---------- Soldes (totaux + par motif) ---------- */}
          <div>
            <h3 className="mb-2 text-sm font-bold text-navy">Soldes</h3>
            {soldes.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-navy/55">
                <Loader2 size={16} className="animate-spin" /> Chargement des soldes…
              </div>
            ) : soldes.error || !soldes.data ? (
              <p className="text-sm text-navy/55">Soldes indisponibles pour le moment.</p>
            ) : (
              <>
                <dl className="grid grid-cols-3 gap-3">
                  <div className="rounded-xl border border-orange-50 bg-cream px-3 py-2.5">
                    <dt className="text-xs font-bold uppercase tracking-wide text-navy/45">
                      Total dû
                    </dt>
                    <dd className="text-sm font-bold text-navy">
                      {formatXof(soldes.data.totalDuXof)}
                    </dd>
                  </div>
                  <div className="rounded-xl border border-orange-50 bg-cream px-3 py-2.5">
                    <dt className="text-xs font-bold uppercase tracking-wide text-navy/45">
                      Total encaissé
                    </dt>
                    <dd className="text-sm font-bold text-green-700">
                      {formatXof(soldes.data.totalPayeXof)}
                    </dd>
                  </div>
                  <div className="rounded-xl border border-orange-50 bg-cream px-3 py-2.5">
                    <dt className="text-xs font-bold uppercase tracking-wide text-navy/45">
                      Reste à payer
                    </dt>
                    <dd className="text-sm font-bold text-coral">
                      {formatXof(soldes.data.totalRestantXof)}
                    </dd>
                  </div>
                </dl>
                {soldes.data.soldes.length === 0 ? (
                  <p className="mt-3 text-sm text-navy/55">Aucun motif facturé.</p>
                ) : (
                  <ul className="mt-3 space-y-2">
                    {soldes.data.soldes.map((s) => (
                      <li
                        key={s.motifId}
                        data-testid={`solde-${s.motifId}`}
                        className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-2.5 ${
                          s.solde
                            ? "border-green-100 bg-green-50/60"
                            : "border-orange-100 bg-orange-50/40"
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-bold text-navy">{s.motifLibelle}</p>
                          <p className="text-xs text-navy/55">
                            Dû {formatXof(s.duXof)} · Payé {formatXof(s.payeXof)}
                          </p>
                        </div>
                        {s.solde ? (
                          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-bold text-green-700">
                            <CheckCircle2 size={12} /> Soldé
                          </span>
                        ) : (
                          <span className="shrink-0 text-sm font-bold text-orange-600">
                            {formatXof(s.restantXof)}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          {/* ---------- Paiements de l'enfant ---------- */}
          <div>
            <h3 className="mb-2 text-sm font-bold text-navy">Paiements</h3>
            {childPaiements.length === 0 ? (
              <p className="text-sm text-navy/55">Aucun paiement pour cet enfant.</p>
            ) : (
              <ul className="space-y-2">
                {childPaiements.map((p) => (
                  <li
                    key={p.id}
                    className="flex items-center justify-between gap-3 rounded-xl border border-orange-50 bg-cream px-4 py-2.5"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-navy">{p.motifLabel}</p>
                      <p className="text-xs text-navy/55">
                        {formatDate(p.datePaiement ?? p.creeLe)} - {p.numeroPaiement}
                      </p>
                    </div>
                    <span className="shrink-0 text-sm font-bold text-navy">
                      {formatXof(p.montant)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ---------- Retirer de mon espace ---------- */}
          <div className="rounded-xl border border-red-100 bg-red-50/60 p-4">
            {confirmRetrait ? (
              <div className="space-y-3">
                <p className="flex items-start gap-2 text-sm text-red-700">
                  <AlertTriangle size={18} className="mt-0.5 shrink-0" />
                  <span>
                    Ceci ne supprime pas l'élève de l'école ; cela le retire seulement de votre
                    tableau de bord.
                  </span>
                </p>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setConfirmRetrait(false)}
                    disabled={detach.isPending}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="button"
                    className="bg-red-600 text-white hover:bg-red-700"
                    onClick={submitRetrait}
                    disabled={detach.isPending}
                  >
                    {detach.isPending ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Retrait...
                      </>
                    ) : (
                      <>
                        <Check size={16} /> Confirmer le retrait
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmRetrait(true)}
                className="inline-flex items-center gap-2 text-sm font-bold text-red-600 hover:underline"
              >
                <Trash2 size={16} /> Retirer de mon espace
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
